/**
 * W-4 · Usage.
 *
 * Deux partis pris :
 *  - les lignes gardent LEURS couleurs (YOG 1 bleu, YOG 2 vert, Ligne 7 violet),
 *    celles de la carte de l'athlète ;
 *  - le ratio alertes déclenchées / posées est mis en avant, parce qu'il mesure
 *    la seule chose qui compte pour cette fonction : la prédiction tient-elle ?
 *    Une alerte posée qui ne sonne jamais est une promesse non tenue.
 */

import { api } from '../lib/api';
import { rangeKey } from '../lib/range';
import type { Range } from '../lib/range';
import { useApi } from '../lib/useApi';
import { formatDateTime, formatDay, formatInt, formatPercent } from '../lib/format';
import { Async, Async2, Card, Kpi } from '../components/ui';
import { BarChart, Funnel, LineChart } from '../charts';
import type { Categorical, FunnelRow } from '../charts';
import { lineIdentity, seriesColor } from '../lib/lines';

/** Routes Expo Router → libellé d'écran lisible par un opérateur du TROC. */
const SCREEN_LABELS: Record<string, string> = {
  '/(tabs)/index': 'Accueil',
  '/(tabs)/map': 'Carte',
  '/(tabs)/lines': 'Lignes',
  '/(tabs)/schedules': 'Horaires',
  '/(tabs)/chat': 'Chat AYO',
  '/(tabs)/support': 'Support',
  '/support/faq': 'FAQ',
  '/support/troc': 'TROC',
  '/support/report': 'Signalement',
  '/support/guide': 'Guide',
  '/onboarding': 'Onboarding',
  '/index': 'Splash',
};

function prettyScreen(key: string, label: string): string {
  return SCREEN_LABELS[key] ?? label;
}

export function UsagePage({ range }: { range: Range }): React.ReactElement {
  const key = rangeKey(range);
  const params = { from: range.from, to: range.to };

  const screens = useApi((s) => api.breakdown('screen', params, s), `screen|${key}`);
  const lines = useApi((s) => api.breakdown('line', params, s), `line|${key}`);
  const events = useApi((s) => api.breakdown('event_name', params, s), `event|${key}`);
  const funnel = useApi((s) => api.funnel(params, s), `funnel|${key}`);
  const alertsSet = useApi((s) => api.timeseries('alerts_set', params, range.bucket, s), `aset|${key}`);
  const alertsFired = useApi((s) => api.timeseries('alerts_fired', params, range.bucket, s), `afired|${key}`);

  const formatX = range.bucket === 'hour' ? formatDateTime : formatDay;

  const funnelRows: FunnelRow[] = (funnel.data?.steps ?? []).map((step, i) => ({
    key: step.name,
    label: step.label,
    value: step.value,
    rate: step.rate,
    color: seriesColor(i),
  }));

  const sum = (points: { v: number }[]): number => points.reduce((acc, p) => acc + p.v, 0);

  return (
    <div className="stack">
      <div className="grid grid-2">
        <Card
          title="Écrans les plus vus"
          hint="Écrans que les athlètes ont réellement consultés pendant la période, du plus vu au moins vu."
        >
          <Async state={screens}>
            {(d) => (
              <BarChart
                title="Écrans les plus vus"
                rows={d.rows.slice(0, 10).map<Categorical>((r, i) => ({
                  key: r.key,
                  label: prettyScreen(r.key, r.label),
                  value: r.value,
                  color: seriesColor(i),
                }))}
                showShare
              />
            )}
          </Async>
        </Card>

        <Card
          title="Lignes les plus consultées"
          hint="Lignes que les athlètes consultent le plus : détail d’une ligne, horaires, appui sur un bus. Les couleurs sont celles de la carte de l’application."
        >
          <Async state={lines}>
            {(d) => (
              <BarChart
                title="Lignes les plus consultées"
                labelWidth={120}
                showShare
                rows={d.rows.map<Categorical>((r, i) => {
                  const identity = lineIdentity(r.key, i);
                  return { key: r.key, label: identity.name, value: r.value, color: identity.color };
                })}
              />
            )}
          </Async>
        </Card>
      </div>

      <Card
        title="Du premier écran à la première alerte"
        hint="Ouvrir l'application → regarder la carte → toucher un bus → demander une alerte. Le pourcentage à droite dit combien passent d'une étape à la suivante : c'est là qu'on voit où l'on perd les athlètes."
      >
        <Async state={funnel}>{() => <Funnel title="Du premier écran à la première alerte" rows={funnelRows} />}</Async>
      </Card>

      <Card
        title="Alertes demandées et envoyées"
        hint="Une alerte est programmée à l'avance pour sonner même application fermée. L'écart entre les deux courbes dit si elles sonnent bien, pas combien d'athlètes utilisent l'application."
      >
        <Async2 a={alertsSet} b={alertsFired}>
          {(setData, firedData) => {
            const totalSet = sum(setData.points);
            const totalFired = sum(firedData.points);
            return (
              <div className="stack">
                <div className="grid grid-kpi">
                  <Kpi
                    label="Alertes demandées"
                    value={formatInt(totalSet)}
                    hint="Un athlète a demandé à être prévenu à l’approche d’un bus."
                  />
                  <Kpi
                    label="Alertes envoyées"
                    value={formatInt(totalFired)}
                    hint="La notification est effectivement partie sur le téléphone de l’athlète."
                  />
                  <Kpi
                    featured
                    label="Part envoyée"
                    value={totalSet > 0 ? formatPercent(totalFired / totalSet, 1) : 'n/d'}
                    hint="Une alerte peut ne pas sonner sans que ce soit un défaut (retirée par l'athlète, ligne sans bus en direct). Si cette part s'effondre, prévenir l'équipe technique."
                  />
                </div>
                <LineChart
                  title="Alertes demandées et envoyées"
                  formatX={formatX}
                  height={240}
                  series={[
                    { key: 'set', label: 'Demandées', color: 'var(--c-blue)', points: setData.points },
                    { key: 'fired', label: 'Envoyées', color: 'var(--c-gold)', points: firedData.points },
                  ]}
                />
              </div>
            );
          }}
        </Async2>
      </Card>

      <Card
        title="Ce que font les athlètes dans l'application"
        hint="Action par action, sur la période."
      >
        <Async state={events}>
          {(d) => (
            <BarChart
              title="Ce que font les athlètes dans l'application"
              labelWidth={150}
              rows={d.rows.slice(0, 14).map<Categorical>((r, i) => ({
                key: r.key,
                label: r.label,
                value: r.value,
                color: seriesColor(i),
              }))}
            />
          )}
        </Async>
      </Card>
    </div>
  );
}
